/**
 * The purpose outlet: each link purpose's own screens, shown once its link
 * session has started. A purpose adds itself here (lazily, so the landing
 * page stays small), and its screens call only that purpose's endpoints
 * under `/api/v1/links/session/…`. A purpose without screens shows the
 * neutral "This link isn't active" state.
 */
import { lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react';

import type { LinkPurpose, LinkSessionInfo } from '@gradex-hire/contracts';

/** What a purpose's screens receive. */
export interface PurposeProps {
  /** The link's ID, as in the URL. */
  linkId: string;
  session: LinkSessionInfo;
  /** Ends the link session and shows the "done" state. */
  onDone: () => void;
}

type Outlet = LazyExoticComponent<ComponentType<PurposeProps>>;

/** Whether the engine's test purpose screens are built in (development and browser tests only). */
const TEST_PURPOSES = import.meta.env.DEV || import.meta.env.VITE_HIRE_GALLERY === 'true';

const testOutlet: Outlet | undefined = TEST_PURPOSES
  ? lazy(() => import('./TestPurpose.js').then((module) => ({ default: module.TestPurpose })))
  : undefined;

/** Each purpose's screens (added by H3.4, H3.5, H3.6, H4.1 and H5.4). */
export const PURPOSE_OUTLETS: Partial<Record<LinkPurpose, Outlet>> = {
  ...(testOutlet ? { ENGINE_TEST: testOutlet, ENGINE_TEST_QR: testOutlet } : {}),
  ORG_INVITATION: lazy(() =>
    import('../organisations/InvitationOutlet.js').then((module) => ({
      default: module.InvitationOutlet,
    })),
  ),
};

/** What a full-page purpose receives: it runs its own code step and screens. */
export interface FullPageProps {
  linkId: string;
  secret: string;
  /** From the code step; empty when the session was already open. */
  maskedEmail: string;
  codeSent: boolean;
  /** Who the link is about (the link's welcome); empty when not known yet. */
  person: string;
  onDone: () => void;
  /** The link stopped working: show the neutral "This link isn't active". */
  onRefused: () => void;
  /** "Report this link", always offered. */
  report: ReactNode;
}

/**
 * Purposes with a page of their own, outside the sign-in layout, from the
 * code step on (H3.4: a referee's first look at Gradex is a hero, not a form).
 */
export const FULL_PAGE_OUTLETS: Partial<
  Record<LinkPurpose, LazyExoticComponent<ComponentType<FullPageProps>>>
> = {
  REFERENCE_REQUEST: lazy(() =>
    import('../references/referee/RefereeExperience.js').then((module) => ({
      default: module.RefereeExperience,
    })),
  ),
  PROFILE_SHARE: lazy(() =>
    import('../profile-share/SharedProfilePage.js').then((module) => ({
      default: module.SharedProfilePage,
    })),
  ),
  PROFILE_QR: lazy(() =>
    import('../profile-qr/ScannedProfilePage.js').then((module) => ({
      default: module.ScannedProfilePage,
    })),
  ),
};
